/**
 * A finished Hide and Seek round's Krall's Accord paths, for the pathways map.
 *
 * Fetched with HNS_ROUND_PATHS_GET: players by room code, admins by game id
 * (which also reaches archived games). Fetched again when the server says a
 * late upload changed the round (HNS_ROUND_PATHS_UPDATED) and after a
 * reconnect. Pass a null round index to fetch nothing.
 */
import { useEffect, useState } from 'react';
import { SocketEvent, } from '@swtorbit/shared-socket-events';
import { useSocketContext } from './SocketProvider';
const TIMEOUT_MS = 10000;
export function useRoundPaths({ roomCode, gameId, roundIndex }) {
    const { socket, isConnected } = useSocketContext();
    const [result, setResult] = useState(null);
    const [version, setVersion] = useState(0);
    const key = `${roomCode}:${gameId ?? ''}:${roundIndex ?? ''}`;
    // A late upload for this round: fetch it again.
    useEffect(() => {
        if (!socket || roundIndex === null)
            return;
        const onUpdated = (update) => {
            if (update.roomCode === roomCode && update.roundIndex === roundIndex)
                setVersion((v) => v + 1);
        };
        socket.on(SocketEvent.HNS_ROUND_PATHS_UPDATED, onUpdated);
        return () => {
            socket.off(SocketEvent.HNS_ROUND_PATHS_UPDATED, onUpdated);
        };
    }, [socket, roomCode, roundIndex]);
    useEffect(() => {
        if (!socket || !isConnected || roundIndex === null)
            return;
        let cancelled = false;
        const request = gameId !== undefined ? { gameId, roundIndex } : { roomCode, roundIndex };
        socket.timeout(TIMEOUT_MS).emit(SocketEvent.HNS_ROUND_PATHS_GET, request, (error, response) => {
            if (cancelled)
                return;
            if (error || !response) {
                setResult({ key, paths: null, error: 'The server did not answer' });
            }
            else {
                setResult({ key, paths: response.success ? response.paths ?? [] : null, error: response.success ? null : response.error ?? 'Could not load the paths' });
            }
        });
        return () => {
            cancelled = true;
        };
    }, [socket, isConnected, roomCode, gameId, roundIndex, key, version]);
    if (roundIndex === null)
        return { paths: null, loading: false, error: null };
    const current = result?.key === key ? result : null;
    return { paths: current?.paths ?? null, loading: !current, error: current?.error ?? null };
}
